import { Tokens } from '@/assets/tokens'
import { Button, Flex, HorizontalTagInput, Input, TagOption, Uploader } from '@/components'
import { UploaderViewer } from '@/components/derived'
import { useFileUploader, useInput, useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Attention, Close } from '@icon-park/react'
import { UpdateBrandRequest } from '@shared/models'
import { memo, useEffect, useState } from 'react'
import { useCompanyContext } from '../../CompanyDetails.context'
import { LogoContainer } from '../../CompanyDetails.styles'
import { DialogContainer, DialogOverlay, ModalBody, ModalHeaderContainer } from './EditBrand.styles'

export const EditBrandModal = memo(Component)

function Component(): JSX.Element | null {
  const { currentPage, companyID, queryParams, changeQueryParam } = useRouting()
  const { brands, reloadBrands, reloadAllBrands } = useCompanyContext()

  const [editBrandId, setEditBrandId] = useState<string | null>(null)

  const [systemLogoChanged, setSystemLogoChanged] = useState(false)
  const [reportLogoChanged, setReportLogoChanged] = useState(false)
  const [originalName, setOriginalName] = useState<string>()

  const [termsTags, setTermsTags] = useState<TagOption[]>([])
  const [originalTermsString, setOriginalTermsString] = useState<string>('')

  const { sendAlert } = Alert.Hook()

  const { isRunning: isUpdatingBrand, run: updateBrand } = Api.Command<void>(Api.Methods.PATCH, '/brands/:id')

  const [fetchLogo] = Api.PromiseQuery<string>(`files/:id`)

  const { value: name, binder: nameBinder, reset: resetName } = useInput()

  const { value: systemLogo, bind: systemLogoBinder, setValue: setSystemLogo } = useFileUploader()

  const { value: reportLogo, bind: reportLogoBinder, setValue: setReportLogo } = useFileUploader()

  useEffect(() => {
    const newEditId = queryParams.editBrandId || null
    setEditBrandId(newEditId)
  }, [queryParams.editBrandId])

  useEffect(() => {
    if (!editBrandId || !brands) return

    const brand = brands.elements.find((b) => b.id === editBrandId)

    if (!brand) return

    resetName(brand.name)
    setOriginalName(brand.name)

    const fetchImage = async (logoID: string, setLogo: (value: any) => void) => {
      if (logoID === 'default-logo-color' || logoID === 'default-logo-gray') {
        return
      }

      const [data] = await fetchLogo({ id: logoID })
      if (data) {
        const [mimePart] = data.split(',')
        const mime = mimePart.split(':')[1].split(';')[0]
        const name = `logo.${mime.split('/')[1]}`
        setLogo({ mime, name, content: data })
      }
    }

    fetchImage(brand.systemLogo, setSystemLogo)
    fetchImage(brand.reportLogo, setReportLogo)
    setSystemLogoChanged(false)
    setReportLogoChanged(false)

    const initialTerms = brand.termsString || ''
    setOriginalTermsString(initialTerms)

    if (initialTerms.trim()) {
      const tags = initialTerms
        .split(';')
        .map((term, index) => ({
          id: `${brand.id}-term-${index}`,
          text: term.trim(),
        }))
        .filter((tag) => tag.text !== '')

      setTermsTags(tags)
    } else {
      setTermsTags([])
    }
  }, [editBrandId, brands])

  function handleClose(): void {
    changeQueryParam({ param: 'editBrandId', value: undefined })
  }

  function onSubmit(): void {
    if (!companyID) return

    if (termsTags.length === 0) {
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'O campo termos é obrigatório!',
      })
      return
    }

    const input: UpdateBrandRequest = {
      companyID: companyID,
    }

    if (name !== originalName) input.name = name
    if (systemLogoChanged && systemLogo) input.systemLogo = systemLogo
    if (reportLogoChanged && reportLogo) input.reportLogo = reportLogo

    const newTermsString = termsTags
      .map((tag) => tag.text.trim())
      .filter((text) => text !== '')
      .join(';')

    if (newTermsString !== originalTermsString) {
      input.termsString = newTermsString
    }

    if (Object.keys(input).length === 1) {
      handleClose()
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Nenhuma alteração detectada',
      })
      return
    }

    updateBrand<UpdateBrandRequest>({
      binds: [['id', editBrandId!]],
      input,
      onSuccess: () => {
        handleClose()
        reloadBrands(currentPage)
        reloadAllBrands()
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: 'Marca atualizada com sucesso',
        })
      },
    })
  }

  if (!editBrandId) {
    return null
  }

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Atualizar Marca</h1>
          <Button.Root $variant="link" onClick={handleClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <ModalBody onSubmit={onSubmit}>
          <Flex $type="column" $align="stretch" $gap={24} $fit>
            <Input label="Nome da Marca" placeholder="Marca" {...nameBinder} required />
            <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
              <LogoContainer>
                <Uploader
                  label="Logo em cores (.svg 417px/128px)"
                  icon={Attention}
                  iconColor="red"
                  {...systemLogoBinder}
                  onUpload={(output) => {
                    systemLogoBinder.onUpload(output)
                    setSystemLogoChanged(true)
                  }}
                  requiredExtensions={['svg']}
                  required
                />
                <UploaderViewer value={systemLogo} />
              </LogoContainer>
              <LogoContainer>
                <Uploader
                  label="Logo em cinza (.svg 417px/128px)"
                  icon={Attention}
                  iconColor="red"
                  {...reportLogoBinder}
                  onUpload={(output) => {
                    reportLogoBinder.onUpload(output)
                    setReportLogoChanged(true)
                  }}
                  requiredExtensions={['svg']}
                  required
                />
                <UploaderViewer value={reportLogo} />
              </LogoContainer>
            </Flex>
            <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
              <HorizontalTagInput
                label="Termos (tags)*"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                onChange={setTermsTags}
                preSelectedTags={termsTags}
                showClearButton={true}
                showBooleanHelp={true}
                exactQuotes={true}
                disabled={isUpdatingBrand}
              />
            </Flex>

            <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
              <Button.Root
                type="button"
                $size={114}
                onClick={handleClose}
                disabled={isUpdatingBrand}
                $loading={isUpdatingBrand}>
                <Button.Text value="Cancelar" />
              </Button.Root>
              <Button.Root
                type="submit"
                $size={114}
                $variant="primary"
                disabled={isUpdatingBrand}
                $loading={isUpdatingBrand}>
                <Button.Text value="Salvar" />
              </Button.Root>
            </Flex>
          </Flex>
        </ModalBody>
      </DialogContainer>
    </DialogOverlay>
  )
}
